import React, { useState, forwardRef, useImperativeHandle } from 'react'
import { gql } from 'graphql-request'
import { Link } from 'react-router-dom'
import {
  Bell,
  MoreHorizontal,
  Check,
  MessageSquare,
  AlertTriangle,
  Undo2,
  Send
} from 'lucide-react'
import { twMerge } from 'tailwind-merge'

import { useQuery, useMutation } from '@hooks/graphql'
import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell } from '@components/ui/table'
import Spinner from '@components/Spinner'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'

import NoResults from './NoResults'
import QuestionModal from './QuestionModal'
import MessageHistoryModal from './MessageHistoryModal'

const EDUCATOR_PROJECT_SUBMISSIONS_QUERY = gql`
  query educatorProjectSubmissions($classroomId: ID!, $educatorProjectId: ID!) {
    educatorProjectSubmissions(classroomId: $classroomId, educatorProjectId: $educatorProjectId) {
      id
      status
      flaggedByModeration
      score
      profile {
        id
        fullName
      }
      classroomEnrollment {
        researchConsent
      }
      blockSubmissions {
        id
        status
        blockId
        content {
          ... on FreeResponseSubmission {
            __typename
            answer
            marks
            automarkedResult
            automarkingError
          }
          ... on QuestionSubmission {
            __typename
            answer {
              label
            }
            correct
            marks
          }
        }
      }
    }
    educatorProject: node(id: $educatorProjectId) {
      ... on EducatorProject {
        id
        name
        totalPossibleScore
        blocks {
          __typename
          blockId
          ... on Reflection {
            __typename
            id
            question
          }
          ... on Question {
            __typename
            id
            label
            markAllocation
            options {
              id
              label
              correct
            }
          }
          ... on FreeResponse {
            __typename
            id
            question
            markAllocation
            markScheme
            workingsRequired
          }
        }
      }
    }
  }
`

const CAN_VIEW_CHAT_HISTORY_QUERY = gql`
  query classroom($id: ID!) {
    classroom(id: $id) {
      research
      organization {
        privateStudentChatsEnabled
      }
    }
  }
`

const BATCH_UPDATE_SUBMISSIONS_MUTATION = gql`
  mutation batchUpdateEducatorProjectSubmissions($input: BatchUpdateEducatorProjectSubmissionsInput!) {
    batchUpdateEducatorProjectSubmissions(input: $input) {
      success
      errors {
        message
      }
    }
  }
`

const formatStatus = status => {
  switch (status) {
    case 'IN_PROGRESS':
      return 'In progress'
    case 'COMPLETED':
      return 'Completed'
    case 'RETURNED':
      return 'Returned'
  }
}

const statusTheme = status => {
  switch (status) {
    case 'COMPLETED':
      return 'bg-green-50 border-green-600 text-green-600 dark:bg-green-900 dark:border-green-600 dark:text-green-200'
    case 'RETURNED':
      return 'bg-purple-50 border-purple-600 text-purple-600 dark:bg-purple-900 dark:border-purple-600 dark:text-purple-200'
    default:
      return 'bg-gray-100 border-gray-300 text-gray-900 dark:bg-gray-900 dark:border-gray-300 dark:text-gray-100'
  }
}

const LessonSubmissionList = forwardRef(({ classroomId, educatorProjectId }, ref) => {
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null)
  const [autoMarkingError, setAutoMarkingError] = useState(false)
  const [selectedChatId, setSelectedChatId] = useState(null)
  const [selectedSubmissionIds, setSelectedSubmissionIds] = useState([])

  const { isLoading, data, refetch } = useQuery({
    queryKey: ['educatorProjectSubmissions', classroomId, educatorProjectId],
    gqlQuery: EDUCATOR_PROJECT_SUBMISSIONS_QUERY,
    variables: { classroomId, educatorProjectId },
    enabled: !!classroomId && !!educatorProjectId
  })

  const { data: { classroom = {} } = {} } = useQuery({
    queryKey: ['canViewChatHistory', classroomId],
    gqlQuery: CAN_VIEW_CHAT_HISTORY_QUERY,
    variables: { id: classroomId },
    enabled: !!classroomId
  })

  const canViewChatHistory = !classroom.organization?.privateStudentChatsEnabled || classroom.research

  const { educatorProjectSubmissions = [], educatorProject = {} } = data || {}
  const { blocks = [], totalPossibleScore = 0 } = educatorProject

  const { mutate: batchUpdateSubmissions, isLoading: isUpdating } = useMutation({
    gqlMutation: BATCH_UPDATE_SUBMISSIONS_MUTATION,
    onSuccess: () => {
      setSelectedSubmissionIds([])
      refetch()
    }
  })

  const toggleAll = () => {
    if (selectedSubmissionIds.length === educatorProjectSubmissions.length) {
      setSelectedSubmissionIds([])
    } else {
      const submissionIds = educatorProjectSubmissions.map(s => s.id)
      setSelectedSubmissionIds(submissionIds)
    }
  }

  const getCSVRows = () => {
    // Process and sort student submissions
    const processedSubmissions = educatorProjectSubmissions.map(sub => {
      // Trim names and split into first and last names
      const fullName = sub.profile.fullName.trim()
      let firstName = ''
      let lastName = ''

      // Extract first and last name
      const nameParts = fullName.split(' ')
      if (nameParts.length >= 2) {
        lastName = nameParts.pop().trim()
        firstName = nameParts.join(' ').trim()
      } else {
        // If only one name, treat it as last name
        lastName = fullName
      }

      return {
        ...sub,
        formattedName: `${lastName}, ${firstName}`,
        lastName,
        firstName
      }
    })

    // Sort by last name, then first name
    processedSubmissions.sort((a, b) => {
      const lastNameComparison = a.lastName.localeCompare(b.lastName)
      if (lastNameComparison !== 0) {
        return lastNameComparison
      }
      return a.firstName.localeCompare(b.firstName)
    })

    // Generate CSV rows from the sorted data
    return processedSubmissions.map(sub => {
      const row = {
        Student: sub.formattedName,
        Status: formatStatus(sub.status),
        'Max Score': `${totalPossibleScore}`,
        'Total Score': `${sub.score}`
      }

      blocks.forEach((block, idx) => {
        let cell = 'FALSE'
        let blockScore = ''
        let blockMaxScore = ''

        // Only add max score for Question and FreeResponse types
        if (block.__typename === 'Question' || block.__typename === 'FreeResponse') {
          blockMaxScore = `${block.markAllocation || ''}`
        }

        const blockSubmission = sub.blockSubmissions.find(b => b.blockId === block.blockId)

        if (blockSubmission) {
          // Consider 'Text' type submissions as TRUE
          if (blockSubmission.status === 'COMPLETED') {
            if (block.__typename === 'Text') {
              cell = 'TRUE'
            } else if (block.__typename === 'Question' || block.__typename === 'FreeResponse') {
              if (blockSubmission.content && (blockSubmission.content.marks !== undefined || blockSubmission.content.answer)) {
                cell = 'TRUE'
                if (blockSubmission.content.marks !== undefined && block.markAllocation) {
                  blockScore = `${blockSubmission.content.marks}`
                }
              }
            }
          } else if (blockSubmission.status === 'IN_PROGRESS') {
            cell = 'In progress'
          }
        }

        row[`Block ${idx + 1}`] = cell

        // Only add score columns for marked block types
        if (block.__typename === 'Question' || block.__typename === 'FreeResponse') {
          row[`Block ${idx + 1} Score`] = blockScore || ''
          row[`Block ${idx + 1} Max Score`] = blockMaxScore
        }
      })

      return row
    })
  }

  useImperativeHandle(ref, () => ({
    getCSVRows
  }))

  return (
    <div className='mt-5'>
      <Choose>
        <When condition={isLoading}>
          <Spinner className='flex items-center justify-center w-full mt-24' />
        </When>

        <When condition={educatorProjectSubmissions.length < 1}>
          <NoResults classroomSelected />
        </When>

        <Otherwise>
          <Button
            onClick={() => batchUpdateSubmissions({ input: { submissionIds: selectedSubmissionIds, status: 'RETURNED' } })}
            disabled={selectedSubmissionIds.length < 1 || isUpdating}
            size="sm"
            className="mb-3"
            loading={isUpdating}
          >
            <Choose>
              <When condition={isUpdating}>
                Updating...
              </When>
              <Otherwise>
                <Send className="size-4" />
                Return submissions&nbsp;
                <If condition={selectedSubmissionIds.length > 0}>
                  <span>({selectedSubmissionIds.length})</span>
                </If>
              </Otherwise>
            </Choose>
          </Button>

          <Button
            onClick={() => batchUpdateSubmissions({ input: { submissionIds: selectedSubmissionIds, status: 'IN_PROGRESS' } })}
            disabled={selectedSubmissionIds.length < 1 || isUpdating}
            variant="outline"
            size="sm"
            className="ml-3 mb-3"
            loading={isUpdating}
          >
            <Choose>
              <When condition={isUpdating}>
                Setting as in progress...
              </When>
              <Otherwise>
                <Undo2 className="size-4" />
                Enable resubmission&nbsp;
                <If condition={selectedSubmissionIds.length > 0}>
                  <span>({selectedSubmissionIds.length})</span>
                </If>
              </Otherwise>
            </Choose>
          </Button>

          {/**
           * Complex height calculation for table scrolling:
           * On desktop: height = full height - breadcrumbs - tabs - toolbar - return button - padding
           * On mobile: height = full height - navbar - breadcrumbs - tabs - toolbar - return button - padding
           **/}
          <div className='lg:max-h-[calc(100vh-84px-50px-45px-28px-2.75rem)] max-h-[calc(100vh-57px-84px-50px-45px-28px-3.5rem)] flex overflow-auto whitespace-nowrap w-full rounded-lg bg-background border border-border [&>*:first-child]:border-0'>
            <Table className='table-auto w-full'>
              <TableHeader>
                <TableRow>
                  <TableHead className='align-middle w-48 sticky left-0 top-0 bg-background z-30'>
                    <div className='relative flex items-center gap-2'>
                      <Checkbox
                        id="select-all"
                        checked={selectedSubmissionIds.length === educatorProjectSubmissions.length}
                        onCheckedChange={toggleAll}
                      />
                      <label htmlFor="select-all">Students</label>
                    </div>
                    <div className='absolute top-0 right-0 bottom-0 w-px bg-border z-20' />
                  </TableHead>
                  <TableHead className='align-middle sticky top-0 bg-background z-20'>
                    Status
                  </TableHead>
                  <TableHead className='text-base align-middle w-32 sticky top-0 bg-background z-20'>Total</TableHead>
                  <For each='block' of={blocks} index='index'>
                    <TableHead key={block.id} className='align-middle text-center sticky top-0 bg-background w-32 z-20'>
                      {index + 1}
                    </TableHead>
                  </For>
                </TableRow>
              </TableHeader>

              <TableBody>
                <For each='submission' of={educatorProjectSubmissions}>
                  <TableRow key={submission.id}>
                    <TableCell className='sticky left-0 bg-background w-48 z-40'>
                      <div className='relative flex items-center gap-2'>
                        <Checkbox
                          id={`select-${submission.id}`}
                          checked={selectedSubmissionIds.includes(submission.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSubmissionIds(prev => [...prev, submission.id])
                            } else {
                              setSelectedSubmissionIds(prev => prev.filter(id => id !== submission.id))
                            }
                          }}
                        />
                        <label htmlFor={`select-${submission.id}`}>{submission.profile.fullName}</label>

                        <If condition={canViewChatHistory && (submission.classroomEnrollment.researchConsent || !classroom.research)}>
                          <Button
                            onClick={() => setSelectedChatId(submission.id)}
                            variant="ghost"
                            size="icon"
                            className='ml-auto'
                          >
                            <MessageSquare className='size-5' />
                          </Button>
                        </If>

                        <If condition={submission.flaggedByModeration}>
                          <div>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className='group ml-2 inline-flex items-center bg-destructive/10 text-destructive p-1 rounded-full'>
                                  <Bell size={16} />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent>
                                Flagged by moderation system
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </If>
                      </div>

                      <div className='absolute top-0 right-0 bottom-0 w-px bg-border z-20' />
                    </TableCell>
                    <TableCell>
                      <span
                        className={twMerge(
                          'rounded-full px-3 py-1',
                          statusTheme(submission.status)
                        )}
                      >
                        {formatStatus(submission.status)}
                      </span>
                    </TableCell>
                    <TableCell className='w-32'>
                      <div className='bg-muted rounded-md px-2 py-1 w-fit border border-border'>
                        {submission.score} / {totalPossibleScore}
                      </div>
                    </TableCell>

                    <For each='block' of={blocks}>
                      <With blockSubmission={submission.blockSubmissions.find(b => b.blockId === block.blockId)}>
                        <Choose>
                          <When condition={blockSubmission?.status === 'IN_PROGRESS'}>
                            <TableCell key={`cell-${submission.id}-${block.blockId}`} className='whitespace-nowrap'>
                              <Link to={`/educators/submissions/${submission.id}`}>
                                <MoreHorizontal className='w-5 h-5 mx-auto' />
                              </Link>
                            </TableCell>
                          </When>

                          <When condition={blockSubmission?.status === 'COMPLETED'}>
                            <TableCell key={`cell-${submission.id}-${block.blockId}`} className='w-32'>
                              <div className='w-full flex justify-center'>
                                <Choose>
                                  <When condition={block.__typename === 'Question' || block.__typename === 'FreeResponse'}>
                                    <With percentage={(blockSubmission.content.marks / block.markAllocation) * 100}>
                                      <Button
                                        onClick={() => {
                                          setSelectedSubmissionId(blockSubmission.id)
                                          setAutoMarkingError(block.__typename === 'FreeResponse' && blockSubmission.content.automarkingError !== null && blockSubmission.content.automarkedResult !== false)
                                        }}
                                        variant="outline"
                                        size="sm"
                                        className={twMerge(
                                          'px-2 py-1',
                                          block.__typename === 'FreeResponse' && blockSubmission.content.automarkingError && blockSubmission.content.automarkedResult !== false
                                            ? 'bg-destructive/10 border border-destructive hover:bg-destructive/10'
                                            : percentage === 100
                                              ? 'bg-green-100 border border-green-600 hover:bg-green-100'
                                              : percentage >= 50
                                                ? 'bg-yellow-100 border border-yellow-600 hover:bg-yellow-100'
                                                : 'bg-destructive/10 border border-destructive hover:bg-destructive/10'
                                        )}
                                      >
                                        {block.__typename === 'FreeResponse' && blockSubmission.content.automarkingError !== null && blockSubmission.content.automarkedResult !== false
                                          ? (
                                            <>
                                              <Tooltip>
                                                <TooltipTrigger asChild>
                                                  <AlertTriangle
                                                    className='w-6 h-6 text-destructive'
                                                  />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                  Automarking issue
                                                </TooltipContent>
                                              </Tooltip>
                                            </>
                                          )
                                          : (
                                            <>
                                              {blockSubmission.content.marks}
                                              <span className='mx-1'>/</span>
                                              {block.markAllocation}
                                            </>
                                          )}
                                      </Button>
                                    </With>
                                  </When>
                                  <Otherwise>
                                    <div className='bg-muted rounded-md px-2 py-1 w-fit border border-border'>
                                      <Check className='w-5 h-5 mx-auto' />
                                    </div>
                                  </Otherwise>
                                </Choose>
                              </div>
                            </TableCell>
                          </When>

                          <Otherwise>
                            <TableCell key={`cell-${submission.id}-${block.blockId}`} />
                          </Otherwise>
                        </Choose>
                      </With>
                    </For>
                  </TableRow>
                </For>
              </TableBody>
            </Table>
          </div>
        </Otherwise>
      </Choose>

      <If condition={!!selectedSubmissionId}>
        <QuestionModal
          refetch={refetch}
          closeModal={() => setSelectedSubmissionId(null)}
          submissionId={selectedSubmissionId}
          autoMarkingError={autoMarkingError}
        />
      </If>

      <If condition={!!selectedChatId}>
        <MessageHistoryModal
          id={selectedChatId}
          closeModal={() => setSelectedChatId(null)}
        />
      </If>
    </div>
  )
})

export default LessonSubmissionList
